import './index.css';
import { config } from '../../config';
import {
  Alert,
  Flex,
  Icon,
  IconButton,
  Text,
  Badge,
  View,
  ScreenReaderContent,
} from '@smarterservices/smarter-ui';
import React, { useEffect, useState } from 'react';
import {
  confirmEndSession,
  dismissToolbarAlert,
  getExtensionState,
  toolbarButtonPressed,
} from '../../helpers/helpers';

const Menu = () => {
  const [toolbarButtonConfig, setToolbarButtonConfig] = useState(null);
  const [toolbarAlert, setToolbarAlert] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 0
  );
  const [buttonContainerMargin, setButtonContainerMargin] = useState('xx-small small none none');

  useEffect(() => {
    // Listen for resizes so we can set the correct icon size
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    const initToolbar = async () => {
      const defaultToolbarConfig = {
        examPassword: true,
        attachments: true,
        sessionInfo: true,
        virtualCalculator: true,
        supportChat: true,
        proctorChat: true,
        liveView: true,
        raiseHand: true,
      };
      const config = await getExtensionState('spToolbarConfig');

      setToolbarButtonConfig(
        Object.assign(defaultToolbarConfig, config?.buttons)
      );

      setToolbarAlert(config?.currentAlert);

    };

    const handleMessage = (event) => {
      if (
        event.source === window &&
        event.data?.source === 'SmarterProctoringExtensionMessenger'
      ) {
        const payload = event.data?.payload;
        if (!payload || !payload?.message) return;

        if (payload.message === 'updateSpToolbarConfig') {
          const buttonConfig = payload.spToolbarConfig.buttons;
          const alert = payload.spToolbarConfig.currentAlert;
          setToolbarButtonConfig(buttonConfig);
          setToolbarAlert(alert);
        }
      }
    };

    initToolbar();
    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Dynamically allow the icons to change sizes based on window width
  const getIconSize = () => {
    if (windowWidth >= 1200) return 'medium';
    if (windowWidth >= 950) return 'small';
    return 'small';
  };
  const iconSize = getIconSize();

  const getButtonLeftPosition = (event) => {
    const buttonElement = event.currentTarget;
    const buttonRect = buttonElement.getBoundingClientRect();
    return buttonRect.left;
  };

  // Logic to show the most critical alert in the toolbar
  const getAlertToShow = () => {
    if (!toolbarAlert?.id) return null;

    const { level, dismissible, message } = toolbarAlert.data;
    const dismissFunction = async () => {
      await dismissToolbarAlert(toolbarAlert.id);
    };
    const closeButtonLabel = dismissible ? 'Close' : null;
    return (
      <View
        as="div"
        className="sp-alert-wrapper"
        overflowX="hidden"
        overflowY="hidden"
        position="relative"
        height="100%"
        width="100%"
      >
        <Alert
          key={toolbarAlert.id}
          margin="none"
          variant={level}
          renderCloseButtonLabel={closeButtonLabel}
          onDismiss={dismissFunction}
          themeOverride={{
            contentPadding: "0 0 0 5px"
          }}
        >
          <div
            style={{
              overflow: 'auto',
              position: 'relative',
              height: '100%',
            }}
          >
            <Text variant='contentSmall' wrap="break-word"
              themeOverride={{
                contentSmall: '0.75rem'
              }}>
              {message}
            </Text>
          </div>
        </Alert>
      </View>
    );
  };
  const alertToShow = getAlertToShow();

  const RenderButton = (props) => {
    if (!toolbarButtonConfig[props.buttonId] && props.buttonId !== 'endSession') {
      return null;
    }
    return (
      <Flex.Item margin={buttonContainerMargin}>
        <Badge
          type="count"
          variant="danger"
          pulse={!!toolbarButtonConfig?.states?.count[props.buttonId]}
          count={toolbarButtonConfig?.states?.count[props.buttonId] || 0}
          themeOverride={{
            countOffset: '0.1rem',
            size: '1.0rem',
            fontSize: '0.5rem',
          }}
        >
          <Flex width="50px" direction="column" alignItems="center" justifyContent="center" gap="none">
            <IconButton
              color="primary-inverse"
              withBorder={true}
              withBackground={false}
              margin="none none xxx-small none"
              padding="none"
              focusColor="inverse"
              interaction='enabled'
              {...props}
            >
              {props.Icon}
            </IconButton>
            <Text as="div" variant='legend' themeOverride={{ legend: '0.6em' }}>{props.label}</Text>
          </Flex>
        </Badge>
      </Flex.Item>
    );
  };

  return (
    <View
      background="brand"
      width="100%"
      height="75px"
      overflowY="hidden"
      overflowX="hidden"
    >
      <Flex
        direction="row"
        height="100%"
        overflowY="hidden"
        overflowX="hidden"
        padding="medium"
        alignItems="center"

      >
        <Flex.Item margin="0 medium 0 0">
          <img
            src="/SecureSessionLogo.svg"
            alt="Secure Session Logo"
            style={{ height: '60px', width: 'auto' }}
          />
        </Flex.Item>
        {toolbarButtonConfig && (
          <>
            <Flex.Item margin="0 medium 0 0">
              <View
                as="div"
                height="65px"
                width="1px"
                background="primary"
                margin="auto 0"
              />
            </Flex.Item>
            <RenderButton
              buttonId="examPassword"
              Icon={<Icon name="IconKey" size="x-small" />}
              label="Password"
              onClick={(event) =>
                toolbarButtonPressed('examPassword', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spExamPasswordIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.examPassword ? "Exam Password" : "Exam Password: Unavailable"}
            />
            <RenderButton
              buttonId="attachments"
              Icon={<Icon name="IconPaperclip" size="x-small" />}
              label="Files"
              onClick={(event) =>
                toolbarButtonPressed('attachments', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spAttachmentMenuIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.attachments ? "Attachments" : "Attachments: Unavailable"}
            />
            <RenderButton
              buttonId="sessionInfo"
              Icon={<Icon name="IconInfoCircle" size="x-small" />}
              label="Info"
              onClick={(event) =>
                toolbarButtonPressed('sessionInfo', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spSessionInfoIframeId',
                })
              }
              screenReaderLabel="Session Information"
            />
            <RenderButton
              buttonId="virtualCalculator"
              Icon={<Icon name="IconCalculatorLine" size="x-small" />}
              label="Calculator"
              onClick={(event) =>
                toolbarButtonPressed('virtualCalculator', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spCalculatorIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.virtualCalculator ? "Virtual Calculator" : "Virtual Calculator: Unavailable"}
            />
            <RenderButton
              buttonId="liveView"
              Icon={<Icon name="IconVideo" size="x-small" />}
              label="Live View"
              onClick={(event) =>
                toolbarButtonPressed('liveView', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spLiveViewIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.liveView ? "Live View" : "Live View: Unavailable"}
            />
            <RenderButton
              buttonId="proctorChat"
              Icon={<Icon name="IconMessageCircleUser" size="x-small" />}
              label="Proctor"
              onClick={(event) =>
                toolbarButtonPressed('proctorChat', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spProctorChatIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.proctorChat ? "Proctor Chat" : "Proctor Chat: Unavailable"}
            />
            {!toolbarButtonConfig?.states?.handRaised &&
              <Flex.Item margin={buttonContainerMargin}>
                <RenderButton
                  buttonId="raiseHand"
                  Icon={<Icon name="IconHandStop" size="x-small" />}
                  label="Raise"
                  onClick={() => toolbarButtonPressed('raiseHand')}
                  screenReaderLabel="Raise Hand"
                />
              </Flex.Item>
            }
            {toolbarButtonConfig?.states?.handRaised &&
              <Flex.Item margin={buttonContainerMargin}>
                <RenderButton
                  buttonId="raiseHand"
                  Icon={<Icon name="IconHandOff" size="x-small" />}
                  color="danger"
                  withBackground={true}
                  label="Lower"
                  onClick={() => toolbarButtonPressed('raiseHand')}
                  screenReaderLabel="Lower Hand"
                />
              </Flex.Item>
            }

            {alertToShow}

            {/* Spacer to push End button to the right */}
            <Flex.Item shouldGrow shouldShrink />

            <RenderButton
              buttonId="supportChat"
              Icon={<Icon name="IconLifebuoy" size="x-small" />}
              label="Support"
              onClick={(event) =>
                toolbarButtonPressed('supportChat', {
                  leftPosition: getButtonLeftPosition(event),
                  sessionUiUrl: config.session.sessionUiUrl,
                  iframeId: 'spSupportChatIframeId',
                })
              }
              screenReaderLabel={toolbarButtonConfig?.supportChat ? "Support Chat" : "Support Chat: Unavailable"}
            />
            <RenderButton
              buttonId="endSession"
              Icon={<Icon name="IconEndLine" size="x-small" />}
              label="End"
              onClick={confirmEndSession}
              screenReaderLabel="End Session"
              withBackground={true}
              color="danger"
            />
          </>
        )}
      </Flex>
    </View>
  );
};

export default Menu;
